import { Component, OnInit, OnDestroy, inject, NgZone } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { AuthService, SessionTimeoutService, TokenRefreshTimerService, AuthSessionTerminatorService, AuthSessionStateService, isPublicAuthFlowUrl } from '@marina/auth';
import { InAppNotificationsStateService, PaymentsHubService } from '@marina/api-client';
import { UserDto } from '@marina/models';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';
import { TopbarNotificationItem } from '@marina/ui';
import { environment } from '../environments/environment';
import { PortalCustomerContextService } from './services/portal-customer-context.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  host: { '[@.disabled]': 'true' },
})
export class AppComponent implements OnInit, OnDestroy {
  currentUser$: Observable<UserDto | null>;
  isAuthenticated$: Observable<boolean>;
  isAuthenticated = false; // Synchronous property for immediate updates
  sidebarOpened = true;
  isMobile = false;
  currentRoute = '';
  isAuthRoute =
    isPublicAuthFlowUrl() ||
    (typeof window !== 'undefined' &&
      !!window.location?.pathname &&
      (window.location.pathname.startsWith('/auth/') ||
        window.location.pathname.startsWith('/onboarding') ||
        window.location.pathname.startsWith('/docusign/return') ||
        window.location.pathname.startsWith('/resources/'))); // Track if we're on an auth route

  connectionLost$ = inject(PaymentsHubService).connectionLost$;
  connectionStatus$ = inject(PaymentsHubService).connectionStatus$;
  private notificationsState = inject(InAppNotificationsStateService);
  notificationItems$ = this.notificationsState.items$;
  notificationUnreadCount$ = this.notificationsState.unreadCount$;
  notificationLoading$ = this.notificationsState.loading$;

  private destroy$ = new Subject<void>();
  private paymentsHub = inject(PaymentsHubService);
  private portalContext = inject(PortalCustomerContextService);
  private mobileQueryList: MediaQueryList | null = null;
  private mobileQueryHandler: ((event: MediaQueryListEvent) => void) | null = null;

  constructor(
    public authService: AuthService,
    private router: Router,
    private ngZone: NgZone,
    private sessionTimeoutService: SessionTimeoutService,
    private sessionTerminator: AuthSessionTerminatorService,
    private authSessionState: AuthSessionStateService,
    // Inject TokenRefreshTimerService to ensure it's initialized (it auto-starts on auth)
    private tokenRefreshTimerService: TokenRefreshTimerService
  ) {
    // Initialize from current auth state
    this.isAuthenticated = this.authService.isAuthenticated();

    // Mobile sidenav should start collapsed.
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      this.mobileQueryList = window.matchMedia('(max-width: 760px)');
      this.isMobile = this.mobileQueryList.matches;
      this.sidebarOpened = this.isAuthenticated && !this.isMobile;

      this.mobileQueryHandler = (_event: MediaQueryListEvent) => {
        this.ngZone.run(() => {
          this.isMobile = !!this.mobileQueryList?.matches;
          if (this.isMobile) {
            this.sidebarOpened = false;
          } else if (this.isAuthenticated) {
            this.sidebarOpened = true;
          }
        });
      };

      if (this.mobileQueryList.addEventListener) {
        this.mobileQueryList.addEventListener('change', this.mobileQueryHandler);
      } else {
        this.mobileQueryList.addListener(this.mobileQueryHandler);
      }
    }
    
    this.currentUser$ = this.authService.currentUser$.pipe(takeUntil(this.destroy$));
    console.log('AppComponent -> this.currentUser$', this.currentUser$);
    this.isAuthenticated$ = this.authService.currentUser$.pipe(
      takeUntil(this.destroy$),
      map((user) => {
        const wasAuthenticated = this.isAuthenticated;
        this.isAuthenticated = user !== null; // Update synchronous property
        
        // Start session monitoring and SignalR when user becomes authenticated
        if (user !== null && !wasAuthenticated) {
          // Idle policy comes from the login/init response (Remember Me disables the idle timer).
          this.sessionTimeoutService.initializeFromSession();
          // Only connect SignalR when the token is currently valid.
          // If the token is expired on page-load, skip here — init() will refresh
          // the token and connect SignalR in its success callback.
          // Note: don't guard on router.url here — after login the URL is still
          // /auth/login when this fires (navigation is async), so the token check
          // is the only reliable gate.
          const tokenExpiresAt = this.authService.getAccessTokenExpiresAt();
          const tokenValid = tokenExpiresAt ? tokenExpiresAt.getTime() > Date.now() : false;
          if (tokenValid) {
            this.paymentsHub.connect().catch((e) => console.warn('[AppComponent] SignalR connect failed', e));
            this.startNotificationsIfEnabled();
          }
        }

        // Stop session monitoring and SignalR when user logs out
        if (user === null && wasAuthenticated) {
          this.sessionTimeoutService.destroy();
          this.paymentsHub.disconnect().catch(() => {});
          this.notificationsState.stop().catch(() => {});
        }
        
        // Ensure sidebar is open when user is authenticated (but keep collapsed on mobile)
        if (!this.isMobile && user !== null && !this.sidebarOpened) {
          this.sidebarOpened = true;
        }
        return user !== null;
      })
    );
    
    // Defer the subscription so BehaviorSubject replay doesn't mutate
    // template-bound state (isAuthenticated, sidebarOpened) within the
    // same change-detection pass — avoids ExpressionChangedAfterItHasBeenCheckedError.
    // The properties are already initialized correctly above from synchronous cache.
    setTimeout(() => this.isAuthenticated$.subscribe());

    this.bindNotificationStartup();

    // Close mobile drawer immediately when navigation starts (before guards/resolvers)
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isMobile && this.sidebarOpened) {
          this.sidebarOpened = false;
        }
      });

    // Track current route for page title and auth route detection
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
        this.isAuthRoute = this.currentRoute.startsWith('/auth/') || 
                          this.currentRoute.startsWith('/onboarding') || 
                          this.currentRoute.startsWith('/docusign/return') ||
                          this.currentRoute.startsWith('/resources/');
      });

  }

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    // Prefer browser pathname so Create Password deep links are recognized before Router.url settles.
    this.isAuthRoute =
      this.currentRoute.startsWith('/auth/') ||
      this.currentRoute.startsWith('/onboarding') ||
      this.currentRoute.startsWith('/docusign/return') ||
      this.currentRoute.startsWith('/resources/') ||
      isPublicAuthFlowUrl();
    const isPublicAuthFlow = isPublicAuthFlowUrl();

    // Check if user is authenticated OR if access token is expired but refresh token is valid
    const isAuthenticated = this.authService.isAuthenticated();
    const accessTokenExpiresAt = this.authService.getAccessTokenExpiresAt();
    const refreshTokenExpiresAt = this.authService.getRefreshTokenExpiresAt();
    const now = Date.now();
    
    console.log('[AppComponent] ngOnInit - Authentication check:', {
      isAuthenticated,
      hasAccessTokenExpiresAt: !!accessTokenExpiresAt,
      hasRefreshTokenExpiresAt: !!refreshTokenExpiresAt,
      currentRoute: this.currentRoute,
    });
    
    // Check if we have valid tokens (even if initData is missing, which happens in new tabs)
    // This check should run even if isAuthenticated() is true (because it checks cached user data, not token validity)
    let shouldCallInit = isAuthenticated;
    let tokenExpiredButRefreshValid = false;
    
    if (accessTokenExpiresAt && refreshTokenExpiresAt) {
      const accessTokenExpired = accessTokenExpiresAt.getTime() <= now;
      const refreshTokenValid = refreshTokenExpiresAt.getTime() > now;
      
      console.log('[AppComponent] Token expiration check:', {
        accessTokenExpired,
        refreshTokenValid,
        accessTokenExpiresAt: accessTokenExpiresAt.toISOString(),
        refreshTokenExpiresAt: refreshTokenExpiresAt.toISOString(),
      });
      
      // Call init() if:
      // 1. Access token is expired but refresh token is valid (need to refresh)
      // 2. OR we have valid tokens but initData is missing (new tab scenario)
      //    - This ensures new tabs can restore authentication state
      if (accessTokenExpired && refreshTokenValid) {
        console.log('[AppComponent] ✅ Access token expired but refresh token valid - will call init() to trigger refresh');
        tokenExpiredButRefreshValid = true;
        shouldCallInit = true;
      } else if (!isAuthenticated && refreshTokenValid) {
        // New tab scenario: tokens exist but initData is missing
        // Call init() to restore authentication state
        console.log('[AppComponent] ✅ Valid refresh token but initData missing (new tab) - will call init() to restore state');
        shouldCallInit = true;
      }
    }
    
    if (!shouldCallInit && !isAuthenticated) {
      // No tokens or no valid refresh token - log warning
      console.warn('[AppComponent] ⚠️ Not calling init() - not authenticated and no valid refresh token');
    }

    // Initialize session timeout service and call init if needed
    if (isPublicAuthFlow) {
      console.log('[AppComponent] Public auth flow detected - skipping init()');
      return;
    }

    if (shouldCallInit) {
      console.log('[AppComponent] Should call init():', {
        isAuthenticated,
        tokenExpiredButRefreshValid,
        willCallInit: true,
      });
      
      // Idle policy comes from the cached/init session policy (Remember Me disables the idle timer).
      this.sessionTimeoutService.initializeFromSession();

      // Refresh session from server so profile name reflects admin updates (e.g. when admin
      // changes client name in Clients table, dashboard header would otherwise show stale cached name)
      // This will also trigger the interceptor to refresh the token if it's expired
      console.log('[AppComponent] 🚀 Calling authService.init() - this should trigger AuthInterceptor');
      this.authService.init().subscribe({
        next: () => {
          console.log('[AppComponent] ✅ Init successful');
          // Connect SignalR now that we have a valid token (covers both page-refresh and
          // the expired-token-but-valid-refresh-token scenario)
          if (!this.isAuthRoute) {
            this.paymentsHub.connect().catch((e) => console.warn('[AppComponent] SignalR connect failed', e));
            this.startNotificationsIfEnabled();
          }
        },
        error: (error) => {
          console.error('[AppComponent] ❌ Init failed:', error);

          // A terminal failure (401/403, or the server saying the session ended) has already been
          // handled by the interceptor, which ended the session and navigated. A temporary one —
          // 5xx, or no response at all — leaves authentication unresolved: we hold what looks like a
          // valid session and simply could not confirm it. Sending the user to Login for that is
          // what turned a page reload during a brief outage into a logout, so instead the shell
          // shows a retry affordance and asks again.
          if (this.authService.authState === 'unresolved') {
            this.sessionUnresolved = true;
            this.scheduleInitRetry();
            return;
          }

          if (!isPublicAuthFlowUrl()) {
            this.router.navigate(['/auth/login']);
          }
        },
      });
    } else {
      console.log('[AppComponent] ⚠️ Not calling init() - not authenticated and no valid refresh token');
    }
  }

  reloadPage(): void {
    window.location.reload();
  }

  /** True while we hold a session we could not confirm; the template offers a retry. */
  sessionUnresolved = false;
  private initRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private initRetryDelayMs = 2000;

  /**
   * Ask again, backing off. Deliberately not bounded by an attempt count: the session is valid as far
   * as anyone knows, so there is nothing to give up on — the user can also press Retry.
   */
  private scheduleInitRetry(): void {
    if (this.initRetryTimer) {
      return;
    }
    this.initRetryTimer = setTimeout(() => {
      this.initRetryTimer = null;
      this.initRetryDelayMs = Math.min(this.initRetryDelayMs * 2, 30000);
      this.retrySessionInit();
    }, this.initRetryDelayMs);
  }

  /** Retry `init()` now (bound to the retry button, and to the backoff timer). */
  retrySessionInit(): void {
    this.authService.init().subscribe({
      next: () => {
        this.sessionUnresolved = false;
        this.initRetryDelayMs = 2000;
        if (this.initRetryTimer) {
          clearTimeout(this.initRetryTimer);
          this.initRetryTimer = null;
        }
      },
      error: () => {
        if (this.authService.authState === 'unresolved') {
          this.scheduleInitRetry();
        } else {
          this.sessionUnresolved = false;
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.mobileQueryList && this.mobileQueryHandler) {
      if (this.mobileQueryList.removeEventListener) {
        this.mobileQueryList.removeEventListener('change', this.mobileQueryHandler);
      } else {
        this.mobileQueryList.removeListener(this.mobileQueryHandler);
      }
    }
  }

  toggleSidebar(): void {
    this.sidebarOpened = !this.sidebarOpened;
  }

  logout(): void {
    // Immediately hide sidebar and update auth state synchronously
    this.isAuthenticated = false;
    this.sidebarOpened = false;
    this.portalContext.clear();

    // Clean up session timeout service and SignalR
    this.sessionTimeoutService.destroy();

    // One place ends sessions, so a user-initiated logout cannot race a 401-driven one into two
    // navigations and two /auth/logout calls. Hubs must be stopped first: POST /auth/logout
    // invalidates the cookie and the still-open WebSocket then 1006s into an automatic reconnect.
    void this.endRealtimeThenSession();
  }

  private async endRealtimeThenSession(): Promise<void> {
    try {
      await this.paymentsHub.disconnect();
    } catch {
      // teardown is best-effort
    }
    try {
      await this.notificationsState.stop();
    } catch {
      // teardown is best-effort
    }
    this.sessionTerminator.end('user-logout', this.authSessionState.sessionId);
  }

  getPageTitle(): string {
    // Extract page title from route
    const route = this.currentRoute.split('?')[0]; // Remove query params
    if (route === '/' || route === '/dashboard') {
      return 'Dashboard';
    }
    // Capitalize and format route name
    const segments = route.split('/').filter((s) => s);
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      return lastSegment
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'Marina Customer Portal';
  }

  onNotificationItemClick(item: TopbarNotificationItem): void {
    void this.notificationsState.markRead(item as any).then(() => {
      if (item.deepLink) {
        void this.router.navigateByUrl(item.deepLink);
      }
    });
  }

  onNotificationMarkAllRead(): void {
    void this.notificationsState.markAllRead();
  }

  onNotificationsMenuOpen(): void {
    void this.notificationsState.refresh();
  }

  private notificationsStartupBound = false;

  /** Start notifications once init/customer context is ready (covers fresh login, not only page refresh). */
  private bindNotificationStartup(): void {
    if (this.notificationsStartupBound || !environment.features?.enableNotifications) return;
    this.notificationsStartupBound = true;

    this.authService.initData$
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data && this.authService.isAuthenticated())
      )
      .subscribe(() => this.startNotificationsIfEnabled());
  }

  private startNotificationsIfEnabled(): void {
    if (!environment.features?.enableNotifications) return;
    if (!this.authService.isAuthenticated()) return;

    const customerId = this.authService.getCustomerId();
    if (!customerId) return;

    this.notificationsState.startCustomer(customerId).catch((e) =>
      console.warn('[AppComponent] Notifications start failed', e)
    );
  }
}
